import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled/macro";
import {
  Button,
  Modal,
  Pill,
  Snackbar,
  Typography,
  LoadingIndicator,
  Banner,
  BannerTypes,
} from "@sunrun/experience-ui-components";
import { PillProps } from "@sunrun/experience-ui-components/lib/components/Pill";
import { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCardCheckout";
import { useMediaQuery } from "react-responsive";
import { CardContent, CardHeader } from "../molecules/Card";
import { findContact, getPrimaryContact } from "../../../utils/contacts";
import { ROLES } from "../../../utils/usageUtils";
import { SlimContactCard } from "../molecules/SlimContactCard";
import { ErrorModal } from "../../../components/molecules/ErrorModal";
import {
  ContactsUnavailableModal,
  isProspectNotQualifiedContactsError,
} from "../../../components/organisms/ContactsUnavailableModal";
import { CreditStatusPillLabels } from "../../util/constants";
import { postCreditCheck } from "../../../services/prospect";
import { rollbar } from "../../../providers/rollbar";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import { Mixpanel } from "providers/mixpanel";
import { Card, CardHeaderDivider } from "components/atoms/Card";
import { useContactsData, useUsageData } from "utils/swrHooks";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { SplatContact } from "providers/Types";
import { useAuthToken } from "hooks/useAuthToken";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { CUSTOMER_SELF_CHECKOUT__CLICKED_RUN_CREDIT_CHECK_IN_CHECKOUT } from "constants/mixpanelEvents";

type SignerType = "primary" | "secondary";

type SplatCreditStatus = {
  isInProgress: boolean;
  isPassed: boolean;
  isManual: boolean;
  isFailed: boolean;
  isPrescreenApproved: boolean;
  isLowFicoPassed: boolean;
};

const mapCreditStatusToPillProps = (
  status: SplatCreditStatus,
  contact?: Contact
): PillProps => {
  const applicationStatus = contact?.creditApplicationStatus;

  if (status.isFailed) {
    return {
      type: "error",
      text: CreditStatusPillLabels.CREDIT_NOT_APPROVED,
    };
  }

  if (status.isLowFicoPassed) {
    return {
      type: "success",
      text: CreditStatusPillLabels.LOW_FICO_APPROVED,
    };
  }

  if (status.isPassed || status.isPrescreenApproved) {
    return {
      type: "success",
      text: CreditStatusPillLabels.CREDIT_APPROVED,
    };
  }

  if (status.isManual && applicationStatus) {
    return {
      type: "warning",
      text: `${
        applicationStatus !== "Withdrawn" ? "Credit " : ""
      }${applicationStatus}`,
    };
  }

  return {
    type: "warning",
    text: CreditStatusPillLabels.CREDIT_NOT_CHECKED,
  };
};

const CreditCheckTaskView: React.FC = () => {
  const {
    navigateToTask,
    navigateToNextTask,
    refreshTasks,
    proposalId,
    isNewHomes,
    userProfile,
  } = useCheckoutContext();
  const { prospectId } = useParams();
  const authKey = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const { prospect } = useUsageData(prospectId);
  const { contacts, contactsError, isContactsValidating, refetch } =
    useContactsData(prospectId as string, authKey, proposalToken);
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });

  const primaryContact = getPrimaryContact(contacts);
  const secondaryContact = findContact(contacts, ROLES.CONTRACTCOSIGNER);
  const primaryCreditCheckUrl = useRef("");
  const secondaryCreditCheckUrl = useRef("");

  const [urlLoading, setUrlLoading] = useState(false);
  const [primaryNotChecked, setPrimaryNotChecked] = useState(false);
  const [secondaryNotChecked, setSecondaryNotChecked] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState<SignerType>();
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState<SignerType>();

  const successMessage =
    showSuccessSnackbar === "primary"
      ? `${primaryContact?.customerFirstName} ${primaryContact?.customerLastName} Credit Check successfully sent`
      : `${secondaryContact?.customerFirstName} ${secondaryContact?.customerLastName} Credit Check successfully sent`;

  const [showErrorSnackbar, setShowErrorSnackbar] = useState<SignerType>();
  const errorMessage =
    showErrorSnackbar === "primary"
      ? `Unable to email ${primaryContact?.customerFirstName} ${primaryContact?.customerLastName} Credit Check`
      : `Unable to email ${secondaryContact?.customerFirstName} ${secondaryContact?.customerLastName} Credit Check`;

  const auxMessage = useMemo(() => {
    if (primaryContact?.creditStatus.isFailed && !secondaryContact?.contactId) {
      return "We need one of the signers to pass the credit check to continue. Add a Secondary signer and run credit check.";
    }

    if (
      primaryContact?.creditStatus.isFailed &&
      secondaryContact?.creditStatus.isFailed
    ) {
      return "Sorry, both signers did not pass credit. You can try replacing the secondary signer or try another financing method.";
    }

    if (
      primaryContact?.creditStatus.isFailed &&
      !secondaryContact?.creditStatus.isPassed
    ) {
      return "We need one of the signers to pass the credit check to continue.";
    }
  }, [
    primaryContact?.creditStatus.isFailed,
    secondaryContact?.creditStatus.isFailed,
    secondaryContact?.creditStatus.isPassed,
    secondaryContact?.contactId,
  ]);

  const navigateToSecondarySigner = () =>
    navigateToTask("confirmNames", { secondaryEdit: true });

  const secondarySignerPrimaryAction = !secondaryContact
    ? {
        title: "Add Secondary Signer",
        action: navigateToSecondarySigner,
        testId: "checkout-credit-check-task-add-secondary-signer",
      }
    : primaryContact?.creditStatus?.isFailed &&
      secondaryContact?.creditStatus?.isFailed
    ? {
        title: "Change secondary signer",
        action: navigateToSecondarySigner,
        testId: "checkout-credit-check-task-change-secondary-signer",
      }
    : undefined;

  useEffect(() => {
    setPrimaryNotChecked(
      !!primaryContact &&
        !primaryContact.creditStatus.isPassed &&
        !primaryContact.creditStatus.isFailed
    );
  }, [primaryContact]);

  useEffect(() => {
    setSecondaryNotChecked(
      !!secondaryContact &&
        !secondaryContact.creditStatus.isPassed &&
        !secondaryContact.creditStatus.isFailed
    );
  }, [secondaryContact]);

  const fetchUrl = useCallback(
    async (contact: Contact, contactType: SignerType) => {
      if (!contact || !contact.contactId || !contact.customerEmail) {
        return;
      }

      let result;

      try {
        setUrlLoading(true);
        result = await postCreditCheck(
          authKey || proposalToken || "",
          prospectId!,
          contact.contactId,
          "INPERSON",
          !!proposalToken
        );
      } catch (e: any) {
        rollbar.error(e);
      } finally {
        if (result?.url) {
          if (contactType === "primary") {
            primaryCreditCheckUrl.current = result?.url;
          } else {
            secondaryCreditCheckUrl.current = result?.url;
          }
        }
        setUrlLoading(false);
      }
    },
    [authKey, proposalToken, prospectId]
  );

  useEffect(() => {
    if (primaryContact) {
      fetchUrl(primaryContact, "primary");
    }
  }, [authKey, fetchUrl, primaryContact]);

  useEffect(() => {
    if (secondaryContact) {
      fetchUrl(secondaryContact, "secondary");
    }
  }, [authKey, fetchUrl, secondaryContact]);

  const handleEmail = useCallback(
    async (contactId: string, contactType: SignerType) => {
      setEmailConfirmOpen(undefined);
      let result;

      try {
        result = await postCreditCheck(
          authKey || proposalToken || "",
          prospectId!,
          contactId,
          "EMAIL",
          !!proposalToken
        );
      } catch (e: any) {
        rollbar.error(e);
      } finally {
        if (
          (result?.success && result.success === true) ||
          result?.message === "Email sent successfully"
        ) {
          setShowSuccessSnackbar(contactType);
        } else {
          setShowErrorSnackbar(contactType);
        }
      }
    },
    [authKey, proposalToken, prospectId]
  );

  const handleRunCredit = useCallback(
    (contactType: SignerType) => {
      if (isCustomer) {
        Mixpanel.track(
          CUSTOMER_SELF_CHECKOUT__CLICKED_RUN_CREDIT_CHECK_IN_CHECKOUT,
          {
            prospectId,
            contactType,
            proposalId,
          }
        );
      }
      const ref = {
        primary: primaryCreditCheckUrl,
        secondary: secondaryCreditCheckUrl,
      }[contactType];

      if (ref.current) {
        const w = window.open(ref.current!, "_blank");
        if (w) {
          w.focus();
        }
      }
    },
    [isCustomer, prospectId, proposalId]
  );

  const creditBannerMessage = useMemo(() => {
    const status = primaryContact?.creditStatus;
    const applicationStatus = primaryContact?.creditApplicationStatus;

    if (status?.isFailed || status?.isPassed || status?.isPrescreenApproved) {
      return;
    }

    if (status?.isManual && applicationStatus) {
      switch (applicationStatus) {
        case "Withdrawn":
          return (
            <>
              The customer either started but did not complete their credit
              application or never began the process after receiving it. Since
              the application was never submitted, we are withdrawing it as an
              added layer of protection for the customer. They will receive an
              email confirming its removal from our database. If they wish to
              proceed with Sunrun, they will need to submit a new credit
              application.
            </>
          );
        case "Pending - Frozen Credit":
          return (
            <>
              The customer's credit is frozen with one of the bureaus. They need
              to unfreeze their credit and use the link in their email to access
              their credit application and continue the process.{" "}
              <a
                href="https://drive.google.com/file/d/1aYVLuiszpstJD5V6oNA2BI7t0GiIdUTp/view?usp=sharing"
                target="_blank"
                rel="noreferrer"
              >
                (Application)
              </a>
            </>
          );
        case "Pending - Compliance Alert":
          return (
            <>
              The credit report triggered a fraud alert with one or more
              bureaus. A Sunrun credit representative will contact the customer
              to verify their information. Do not re-run the credit check.
            </>
          );
        case "Pending - Information Discrepancy":
          return (
            <>
              The Credit Application requires a manual review. This is most
              likely due to a mismatched First or Last Name. Check out the{" "}
              <a
                href="https://docs.google.com/document/d/1tNH5hyjQKOLrYqeKsuOWHYVNdlSKLl9V4SKtSUXpMSY/edit?usp=sharing"
                target="_blank"
                rel="noreferrer"
              >
                Frequently Asked Questions
              </a>{" "}
              to resolve these issues without waiting for the credit team.
            </>
          );
        case "No Hit":
          return (
            <>
              This Credit Application Status requires action from either the
              Customer or Sales Rep. The issue is most likely related to the
              Date of Birth or Social Security Number. The customer can use the
              link in their email to review and update their application. Please
              ensure they verify that all information is accurate.
            </>
          );
      }
    }
  }, [primaryContact?.creditStatus, primaryContact?.creditApplicationStatus]);

  const renderFooter = (contactType: SignerType, disabled: boolean) => {
    return (
      <StyledFooter isMobile={isMobile}>
        <Button
          color="primary"
          size="sm"
          onClick={() => {
            handleRunCredit(contactType);
          }}
          style={{ minWidth: 125 }}
          data-testid={`checkout-credit-check-run-${contactType}`}
          disabled={disabled}
        >
          {urlLoading ? (
            <LoadingIndicator showLoadingMessage={false} />
          ) : (
            "Run Credit Check"
          )}
        </Button>
        <Button
          data-testid={`checkout-run-credit-check-task-email-${contactType}`}
          color="secondary"
          size="sm"
          onClick={() => setEmailConfirmOpen(contactType)}
          disabled={disabled}
        >
          Email
        </Button>
      </StyledFooter>
    );
  };

  const renderEmailModal = (contact: SplatContact, contactType: SignerType) => (
    <StyledModal
      testId="email-credit-check-modal"
      hideIcon={true}
      title="Email Credit Check"
      onClose={() => setEmailConfirmOpen(undefined)}
      primaryButton={{
        testId: "credit-check-modal-send-email",
        text: "Send email",
        onClick: () => handleEmail(contact.contactId, contactType),
      }}
      secondaryButton={{
        testId: "credit-check-modal-cancel",
        text: "Cancel",
        onClick: () => setEmailConfirmOpen(undefined),
      }}
    >
      <Typography className={"email-confirm"}>
        The credit check email will be sent to the email address below.
      </Typography>
      <SlimContactCard contact={contact} isMobile={isMobile}>
        <span>{contact.customerEmail}</span>
      </SlimContactCard>
    </StyledModal>
  );

  const showFooter = !contactsError && !isContactsValidating && !!contacts;

  useCheckoutFooter(
    showFooter ? (
      <CheckoutFooter buttonText="Continue" onClick={navigateToNextTask} />
    ) : null,
    [showFooter, navigateToNextTask]
  );

  if (contactsError && isProspectNotQualifiedContactsError(contactsError)) {
    return (
      <ContactsUnavailableModal
        prospect={prospect}
        userProfile={userProfile}
        errorMessage={contactsError?.response?.body?.message}
      />
    );
  }

  if (contactsError) {
    return (
      <ErrorModal
        context={"Error fetching contacts credit status"}
        error={contactsError}
        onClose={refetch}
        onRetry={refetch}
      />
    );
  }

  if (isContactsValidating) {
    return <LoadingIndicator fullScreen />;
  }

  return (
    <>
      <StyledTypography isMobile={isMobile}>
        Once you’ve completed the credit check, it might take several minutes to
        update the status.
        <InlineLink
          onClick={() => {
            refetch();
            refreshTasks();
          }}
        >
          Please refresh this page
        </InlineLink>
      </StyledTypography>
      <GridLayoutDiv isMobile={isMobile}>
        {primaryContact && (
          <Card isMobile={isMobile}>
            <CardHeader isMobile={isMobile}>
              <Typography size={20}>Primary Signer</Typography>
            </CardHeader>
            <CardHeaderDivider isMobile={isMobile} />
            <CardContent isMobile={isMobile}>
              <SlimContactCard contact={primaryContact} isMobile={isMobile}>
                <Pill
                  {...mapCreditStatusToPillProps(
                    primaryContact.creditStatus,
                    primaryContact
                  )}
                />
              </SlimContactCard>
              {primaryNotChecked &&
                renderFooter(
                  "primary",
                  !(primaryContact && primaryContact.customerEmail)
                )}
            </CardContent>
            <Banner
              show={isNewHomes}
              showClose={false}
              bannerType={BannerTypes.InfoLight}
            >
              <Typography>
                New Homes customers need to complete a soft credit check in
                order to move forward with signing their agreement.{" "}
                <b>Please note:</b> This is a soft credit check and will not
                impact your credit score. Regardless of your credit result, you
                will be able to move forward and sign your agreement. Your
                Concierge will follow-up if additional information is needed.
              </Typography>
            </Banner>
            {emailConfirmOpen === "primary" &&
              renderEmailModal(primaryContact, "primary")}
          </Card>
        )}

        {creditBannerMessage && (
          <Banner show showClose={false} bannerType={BannerTypes.WarningLight}>
            <Typography>{creditBannerMessage}</Typography>
          </Banner>
        )}

        <Snackbar
          open={!!showSuccessSnackbar}
          autoHideDuration={4000}
          colorScheme="light"
          message={successMessage}
          onClose={() => setShowSuccessSnackbar(undefined)}
          type="success"
        />
        <Snackbar
          open={!!showErrorSnackbar}
          autoHideDuration={4000}
          colorScheme="light"
          message={errorMessage}
          onClose={() => setShowErrorSnackbar(undefined)}
          type="error"
        />
        {secondaryContact && (
          <Card isMobile={isMobile}>
            <CardHeader isMobile={isMobile}>
              <Typography size={20}>Secondary Signer</Typography>
            </CardHeader>
            <CardHeaderDivider isMobile={isMobile} />
            <CardContent isMobile={isMobile}>
              <SlimContactCard contact={secondaryContact} isMobile={isMobile}>
                <Pill
                  {...mapCreditStatusToPillProps(
                    secondaryContact.creditStatus,
                    secondaryContact
                  )}
                />
              </SlimContactCard>
              {secondaryNotChecked &&
                renderFooter(
                  "secondary",
                  !(secondaryContact && secondaryContact.customerEmail)
                )}
            </CardContent>
            {emailConfirmOpen === "secondary" &&
              renderEmailModal(secondaryContact, "secondary")}
          </Card>
        )}

        {auxMessage ? (
          <Banner
            show
            showClose={false}
            bannerType={BannerTypes.ErrorLight}
            bannerTestId="checkout-credit-check-task-need-one-to-pass"
            title={auxMessage}
            primaryAction={secondarySignerPrimaryAction}
          />
        ) : (
          !secondaryContact && (
            <StyledButton
              isMobile={isMobile}
              color="secondary"
              size="sm"
              onClick={navigateToSecondarySigner}
              data-testid="checkout-credit-check-task-add-secondary-signer"
            >
              Add Secondary Signer
            </StyledButton>
          )
        )}
      </GridLayoutDiv>
    </>
  );
};

const StyledTypography = styled(Typography)<{ isMobile?: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    padding-bottom: 16px !important;
  `}
`;

const StyledButton = styled(Button)<{ isMobile?: boolean }>`
  max-width: 212px;
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

const GridLayoutDiv = styled.div<{ isMobile: boolean }>`
  display: ${(props) => (props.isMobile ? "flex" : "grid")};
  margin-top: ${(props) => (props.isMobile ? "0px" : "32px")};
  grid-row-gap: ${(props) => (props.isMobile ? "16px" : "32px")};
  margin-bottom: ${(props) => (props.isMobile ? "16px" : "32px")};
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
  `}
`;

const StyledFooter = styled.footer<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "column" : "row-reverse")};
  gap: 20px;

  & Button {
    padding: 8px 48px;
  }
`;

const StyledModal = styled(Modal)`
  .sr-modal-container {
    max-width: 100%;
  }

  .sr-button-tertiary {
    flex-direction: column;
  }

  & .email-confirm {
    margin-bottom: 24px;
  }
`;

const InlineLink = styled.span`
  text-decoration: underline;
  font-weight: 200;
  cursor: pointer;
  margin-right: 10px;
  margin-left: 2px;
`;

export { CreditCheckTaskView, mapCreditStatusToPillProps };
